/* eslint-disable react-refresh/only-export-components -- this module exports
 * the TableLayoutProvider component ALONGSIDE the useTableLayout hook + the
 * SavedView/TableLayout types that consumers (EntityTable) import. Same
 * colocated-API pattern as columnListFilter.tsx / MultiColumnFilter.tsx. */
/**
 * Per-table layout + saved-views store for the MAIN-APP EntityTable
 * (Round-4 Area D — POWER pass / owner #6).
 *
 * This is the main-app analogue of the ops table's `ops/lib/uiPrefs.ts`
 * `tables[tableId]` slice: it persists each table's POWER state — column
 * visibility, column order, column widths, and named saved views — keyed by the
 * table's `filterTableKey` (the same stable id pages already pass to
 * EntityTable). It deliberately mirrors the ops store's shape so the two tables
 * behave identically ("no table weaker than another"):
 *
 *   - localStorage is the always-on backing (key `clickpay.table.layouts.v1`),
 *     so the controls work in dev (CLICKPAY_AUTH=off) and offline exactly as
 *     the ops table's localStorage uiPrefs does.
 *   - When a user session exists, layouts hydrate from / persist to the
 *     server (cp_table_layouts, migration 085, /api/table-layout/*), so a
 *     user's column setup + saved views survive deploys and follow them across
 *     devices/origins — same rationale + per-user pattern as the sidebar store.
 *
 * The store is OPTIONAL for EntityTable: when no provider is mounted (e.g. a
 * page rendered in isolation, or a table with no `tableId`), the hook returns a
 * localStorage-only fallback so the table still functions — persistence just
 * doesn't sync to the server. This matches `useUIPrefs`'s no-provider fallback.
 */
import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
  type ReactNode,
} from 'react'
import { apiTableLayout, type TableLayoutBlob } from './api'

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

/** A named, reusable snapshot of a table's filter + sort + column layout.
 *  Mirrors the ops `SavedView` shape (slug/label/created_at + the snapshot)
 *  minus the heatmap/density bits the main-app table doesn't carry. */
export interface SavedView {
  /** url-ish slug like "critical-acos" */
  slug: string
  /** human label like "Critical ACoS" */
  label: string
  /** ISO created-at timestamp. */
  created_at: string
  /** Serialized colFilter (FilterSpec). Stored opaque to avoid a circular
   *  import; the table casts it back to FilterSpec on apply. */
  filter?: unknown
  /** Sort snapshot — single column for the main-app table. */
  sort?: { key: string; dir: 'asc' | 'desc' } | null
  /** Visible column map (colKey → visible). Missing keys default visible. */
  visibility?: Record<string, boolean>
  /** Column order (list of column keys). */
  columnOrder?: string[]
  /** Global search text. */
  search?: string
}

/** Per-table layout slice — the POWER state for one table id. */
export interface TableLayout {
  /** colKey → visible. Missing = visible. */
  visibility?: Record<string, boolean>
  /** Ordered list of column keys (display order). */
  columnOrder?: string[]
  /** colKey → pixel width (resize). Missing = auto. */
  columnWidths?: Record<string, number>
  /** Named saved views. */
  views?: SavedView[]
  /**
   * The `columnOrderRev` (see EntityTable's prop of the same name) that was
   * current when `columnOrder` was last written by a user drag. A page can
   * bump its own rev after changing its DEFAULT column order so that an
   * employee's stale saved order (captured under an older rev) stops
   * overriding the new default — but a drag AFTER the bump re-stamps this to
   * the new rev and sticks again. Missing/undefined reads as rev 1, so pages
   * that never pass `columnOrderRev` are unaffected (their saved orders never
   * looked stale to a gate that stays at the default).
   */
  orderRev?: number
  /**
   * The `columnWidthsRev` current when `columnWidths` was last written by a
   * user resize. Exact twin of `orderRev`, for the same reason and with the
   * same semantics.
   *
   * WHY IT HAD TO EXIST (2026-09-04): three separate feedback fixes in this
   * batch were width fixes — #32's clipped Suggestions action buttons, #39's
   * CPC band cell overlapping the next column, #47's Pacing column — and all
   * three were INVISIBLE to the one person who reported them. `effColumnWidth`
   * lets a persisted `columnWidths[key]` beat the newly declared width, and
   * the owner has stored widths on exactly these tables (verified in prod
   * `cp_table_layouts`: `campaigns` carries `budget_spent_bucket: 101`, which
   * is #47's column). A fix nobody can see is not a fix.
   */
  widthsRev?: number
  /**
   * PR 9a -- per-COLUMN width versions: colKey -> the column's `widthRev` that
   * was current when that column's width was last written by a resize. The
   * finer twin of `widthsRev`: a code fix to one column's default width no
   * longer wipes every saved width on the page. Missing reads as rev 1.
   */
  columnWidthRevs?: Record<string, number>
  /**
   * PR 9b -- per-COLUMN visibility versions: colKey -> the column's
   * `visibilityRev` that was current when that column's show/hide was last
   * written. The exact twin of `columnWidthRevs`: a code change to one
   * column's DEFAULT visibility reaches people who toggled it, without wiping
   * the rest of their saved visibility. Missing reads as rev 1.
   */
  columnVisibilityRevs?: Record<string, number>
}

/**
 * Read-side staleness gate for a saved `columnOrder` (Round-4 Area D follow-up
 * — column-defaults delivery). A page bumps `columnOrderRev` when it changes
 * its DEFAULT column order in code; a `TableLayout` whose `orderRev` is older
 * than that (or missing, which reads as rev 1) is treated as not having a
 * saved order at all, so the new default wins — until a drag or a saved-view
 * apply re-stamps `orderRev` to the current value and the saved order sticks
 * again. Pure and side-effect-free: it never reads or writes storage, so
 * calling it is always safe against the layout store's async server hydrate.
 * A page that never passes `columnOrderRev` (the default) never gates
 * anything — every existing table keeps behaving exactly as before.
 */
export function isColumnOrderStale(layout: TableLayout, columnOrderRev?: number): boolean {
  return columnOrderRev != null && (layout.orderRev ?? 1) < columnOrderRev
}

/**
 * Read-side staleness gate for saved `columnWidths` — the exact twin of
 * `isColumnOrderStale`, and deliberately written as its own function rather
 * than a generalised one: the two revs are independent (a page can change a
 * default width without touching order, and did), and a shared helper would
 * invite bumping both when only one moved.
 *
 * Same contract: a page that never passes `columnWidthsRev` gates nothing, so
 * every existing table keeps behaving exactly as before. A resize AFTER the
 * bump re-stamps `widthsRev` and the employee's width sticks again — the fix
 * reaches them once, and then they stay in charge.
 */
export function areColumnWidthsStale(layout: TableLayout, columnWidthsRev?: number): boolean {
  return columnWidthsRev != null && (layout.widthsRev ?? 1) < columnWidthsRev
}

/**
 * The saved widths that still apply (PR 9a, L5b): the page-wide gate first
 * (`areColumnWidthsStale`, unchanged -- one bump still hides every saved
 * width), then each column's own `widthRev`: a width saved under an older rev
 * than the column's current one reads as absent, the other columns keep theirs.
 * `colRevs` maps colKey -> the column's code `widthRev` (absent = 1). Never
 * mutates the stored blob; a resize re-stamps just that column.
 */
export function effectiveSavedWidths(
  layout: TableLayout, pageRev: number | undefined, colRevs: Record<string, number | undefined>,
): Record<string, number> {
  if (areColumnWidthsStale(layout, pageRev)) return {}
  const saved = layout.columnWidths ?? {}
  let out: Record<string, number> | null = null
  for (const key of Object.keys(saved)) {
    const want = colRevs[key]
    if (want == null) continue
    if ((layout.columnWidthRevs?.[key] ?? 1) < want) {
      if (!out) out = { ...saved }
      delete out[key]
    }
  }
  return out ?? saved
}

/**
 * The saved show/hide that still applies (PR 9b, I9-5): each saved key whose
 * stamped rev (`columnVisibilityRevs[key]`, missing = 1) is older than the
 * column's current `visibilityRev` (`colRevs[key]`) reads as absent, so the
 * column's new default (`defaultHidden`) wins -- until the next toggle
 * re-stamps it. Columns that declare no rev are never gated. Pure: never
 * mutates the stored blob.
 */
export function effectiveSavedVisibility(
  layout: TableLayout, colRevs: Record<string, number | undefined>,
): Record<string, boolean> {
  const saved = layout.visibility ?? {}
  let out: Record<string, boolean> | null = null
  for (const key of Object.keys(saved)) {
    const want = colRevs[key]
    if (want == null) continue
    if ((layout.columnVisibilityRevs?.[key] ?? 1) < want) {
      if (!out) out = { ...saved }
      delete out[key]
    }
  }
  return out ?? saved
}

/**
 * Merge a saved column order against the table's currently DECLARED columns
 * (feedback #17). Used on the read path (EntityTable's `orderedColumns`) so a
 * newly-added pinned column — a leading `__actions`, but see below — reaches
 * every saved layout without ever rewriting the stored blob.
 *
 * Semantics:
 *   (a) keep every saved key that still exists in `declared`, in saved order
 *       (a pinned key already present in `saved` keeps its saved slot — this
 *       does NOT re-pin it to its declared position on every render);
 *   (b) walking `declared` in order, splice each PINNED key that is missing
 *       from `saved` in at `Math.min(itsDeclaredIndex, out.length)` — i.e. as
 *       close to its declared position as the (possibly shorter) kept list
 *       allows, never past the current end;
 *   (c) append every remaining missing NON-pinned key at the end, preserving
 *       today's append semantics for brand-new columns (EntityTable.tsx's
 *       orderedColumns memo, ~:422-424).
 *
 * WHY declared-index and not the ops table's front-loading variant:
 * frontend/src/ops/components/EntityTable.tsx has its own
 * `normalizeColumnOrder` (~:335-344) that front-loads ALL
 * `hideFromVisibilityMenu` columns ahead of the saved order. Porting that
 * verbatim here would relocate pages/Suggestions.tsx's `_action` columns
 * (:558, :588) — also `hideFromVisibilityMenu: true`, but declared LAST on
 * purpose — to the FRONT of every table with a persisted layout (tableId
 * `suggests_${activeTab}`, Suggestions.tsx:777). Declared-index insertion
 * serves both cases from one function: a pinned column declared first (the
 * new leading `__actions`) lands first; a pinned column declared last (`_action`)
 * lands last — each keyed off where the page actually put it in `declared`,
 * not off pinned-ness alone.
 */
export function normalizeColumnOrder(saved: string[], declared: { key: string; pinned?: boolean }[]): string[] {
  const knownKeys = new Set(declared.map((d) => d.key))
  // (a) — keys still declared, in saved order. A pinned key surviving this
  // filter keeps the slot the user (or a prior normalization) left it in.
  const out = saved.filter((k) => knownKeys.has(k))
  const seen = new Set(out)
  // (b) — missing pinned keys, inserted near their declared position. `out`
  // is mutated in place, so a later pinned key's `out.length` already
  // reflects any earlier splice in this same pass.
  declared.forEach((d, i) => {
    if (d.pinned && !seen.has(d.key)) {
      out.splice(Math.min(i, out.length), 0, d.key)
      seen.add(d.key)
    }
  })
  // (c) — remaining missing non-pinned keys, declared order, at the end.
  for (const d of declared) {
    if (!seen.has(d.key)) { out.push(d.key); seen.add(d.key) }
  }
  return out
}

type LayoutMap = Record<string, TableLayout>

// ─────────────────────────────────────────────────────────────────────────────
// localStorage backing
// ─────────────────────────────────────────────────────────────────────────────

const STORAGE_KEY = 'clickpay.table.layouts.v1'

function loadLocal(): LayoutMap {
  if (typeof window === 'undefined') return {}
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    return raw ? (JSON.parse(raw) as LayoutMap) : {}
  } catch {
    return {}
  }
}

function saveLocal(map: LayoutMap): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(map))
  } catch {
    // quota / unavailable — best-effort.
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Context
// ─────────────────────────────────────────────────────────────────────────────

interface TableLayoutApi {
  /** Layout for a table id; never undefined (empty object when none). */
  layout: (tableId: string) => TableLayout
  /** Replace/merge a table's layout (functional or value form). Persists to
   *  localStorage immediately and to the server (debounced) when authed. */
  setLayout: (tableId: string, next: TableLayout | ((cur: TableLayout) => TableLayout)) => void
}

const Ctx = createContext<TableLayoutApi | null>(null)

const EMPTY_LAYOUT: TableLayout = Object.freeze({}) as TableLayout

export function TableLayoutProvider({ children }: { children: ReactNode }) {
  const [map, setMap] = useState<LayoutMap>(() => loadLocal())
  // Tracks which table ids changed since the last server flush, so we only
  // PUT the tables the user actually touched (not the whole hydrated set).
  const dirtyRef = useRef<Set<string>>(new Set())
  const flushTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Mirror the latest map into a ref so the debounced server flush reads
  // current values without listing `map` as a dep (which would re-arm the
  // timer on every change).
  const mapRef = useRef<LayoutMap>(map)

  // Persist to localStorage on every change (synchronous, always-on backing).
  useEffect(() => { mapRef.current = map; saveLocal(map) }, [map])

  // Hydrate from the server ONCE on mount. When auth is off the endpoint
  // returns {layouts:{}} and we keep the localStorage copy. Server values win
  // for tables the server knows about (they survived a deploy / came from
  // another device); tables only in localStorage are preserved.
  useEffect(() => {
    let cancelled = false
    apiTableLayout.getAll()
      .then((res) => {
        if (cancelled) return
        const server = res?.layouts
        if (!server || Object.keys(server).length === 0) return
        setMap((cur) => {
          const merged: LayoutMap = { ...cur }
          for (const [tid, blob] of Object.entries(server)) {
            merged[tid] = blob as TableLayout
          }
          return merged
        })
      })
      .catch(() => { /* offline / dev — localStorage is the source of truth */ })
    return () => { cancelled = true }
  }, [])

  const flush = useCallback(() => {
    const ids = Array.from(dirtyRef.current)
    dirtyRef.current.clear()
    if (ids.length === 0) return
    // PUT each dirty table from the latest map. In dev mode (auth off) the PUT
    // is a server no-op (persisted:false) — harmless.
    const cur = mapRef.current
    for (const tid of ids) {
      const blob = cur[tid]
      if (!blob) continue
      apiTableLayout.put(tid, blob as TableLayoutBlob).catch(() => { /* offline */ })
    }
  }, [])

  const setLayout = useCallback<TableLayoutApi['setLayout']>((tableId, next) => {
    setMap((cur) => {
      const curT = cur[tableId] ?? {}
      const nextT = typeof next === 'function' ? next(curT) : next
      // Skip a no-op write so identical re-renders don't churn the server.
      if (JSON.stringify(curT) === JSON.stringify(nextT)) return cur
      dirtyRef.current.add(tableId)
      return { ...cur, [tableId]: nextT }
    })
    // Debounce the server flush so a drag-resize burst is one PUT.
    if (flushTimer.current) clearTimeout(flushTimer.current)
    flushTimer.current = setTimeout(flush, 800)
  }, [flush])

  const layout = useCallback<TableLayoutApi['layout']>(
    (tableId) => map[tableId] ?? EMPTY_LAYOUT,
    [map],
  )

  const api = useMemo<TableLayoutApi>(() => ({ layout, setLayout }), [layout, setLayout])
  return <Ctx.Provider value={api}>{children}</Ctx.Provider>
}

/**
 * Table-layout store hook. When no provider is mounted, returns a
 * localStorage-only fallback (server sync disabled) so the table still works in
 * isolation — same graceful-degrade contract as `useUIPrefs`.
 */
export function useTableLayout(): TableLayoutApi {
  const v = useContext(Ctx)
  // Fallback: a self-contained localStorage store with no server sync. Created
  // lazily and memoised so the no-provider path is still reactive within the
  // component that uses it.
  const [fallbackMap, setFallbackMap] = useState<LayoutMap>(() => (v ? {} : loadLocal()))
  const fallbackApi = useMemo<TableLayoutApi>(() => ({
    layout: (tableId) => fallbackMap[tableId] ?? EMPTY_LAYOUT,
    setLayout: (tableId, next) => {
      setFallbackMap((cur) => {
        const curT = cur[tableId] ?? {}
        const nextT = typeof next === 'function' ? next(curT) : next
        if (JSON.stringify(curT) === JSON.stringify(nextT)) return cur
        const merged = { ...cur, [tableId]: nextT }
        saveLocal(merged)
        return merged
      })
    },
  }), [fallbackMap])
  return v ?? fallbackApi
}
